/* eslint-disable no-param-reassign */
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  Modifier,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { Popover } from '@headlessui/react';
import {
  AdjustmentsHorizontalIcon,
  CubeIcon,
  SparklesIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import {
  useAppDispatch,
  useAppSelector,
  useConfigurationValidation
} from 'app/hooks';
import store from 'app/store';
import { useContextMenu } from 'components/common/context-menu-manager';
import DuctedReadinessIndicator from 'components/common/ducted-readiness-indicator';
import View3DHelpPopup from 'components/common/view-3d-help-popup';
import ConfigurationSelectionModal from 'components/configuration-page/configuration-selection-modal';
import ConfigurationTabs from 'components/configuration-page/configuration-tabs';
import ConfigurationsDragOverlay from 'components/configuration-page/configurations-drag-overlay';
import DesignRulePanel from 'components/configuration-page/design-rule-panel';
import LinesetSummaryPanel from 'components/configuration-page/lineset-summary-panel';
import PtacSystemsListModal from 'components/configuration-page/ptac-systems-list-modal';
import DuctedPairsSummaryCard from 'components/configuration-page/ducted-pairs-summary-card';
import DuctedSystemsSummaryCard from 'components/configuration-page/ducted-systems-summary-card';
import PtacSystemsSummaryCard from 'components/configuration-page/ptac-systems-summary-card';
import PtacUnitsPanel from 'components/configuration-page/ptac-units-panel';
import ResetLinesetsButton from 'components/configuration-page/reset-linesets-button';
import SystemsListModal from 'components/configuration-page/systems-list-modal';
import ZoneCondenserMap from 'components/configuration-page/zone-condenser-map';
import Button from 'components/inputs/button';
import StepSidebar from 'components/layout/step-sidebar';
import Tooltip from 'components/layout/tooltip';
import StructureGrid from 'components/structure-page/2d/structure-grid';
import Grid3DScene from 'components/structure-page/3d/grid-3d-scene';
import LevelPicker from 'components/structure-page/level-picker';
import AirHandlerList from 'components/structure-page/lists/air-handler-list';
import CondenserList from 'components/structure-page/lists/condenser-list';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  GridObjectType,
  GridSpaceDataType,
  SidebarSectionType,
  ZoneOutlineData,
  ZoneRefType,
  ZoneType
} from 'data/types';
import { setPendingEvaluation } from 'features/chatbot';
import {
  addProtectedConfigurationIdDuringGeneration,
  addSpacesToZone,
  autoGenerateLinesets,
  createPtacZone,
  createZoneOnCondenser,
  deleteEmptyZones,
  deleteZone,
  removeSpaceFromZone,
  resetConfigurationEquipment,
  setActiveConfig
} from 'features/configurations';
import { reorderLevels } from 'features/structure';
import { setSystemOptions, setSystemOptionsForConfig } from 'features/systems';
import {
  setActiveConfigurationStepConfigId,
  setActiveConfigurationStepId,
  setActiveLevelIndex,
  setActiveTab,
  setEquipmentConfigSelectionSignature,
  setShowChatPanel,
  updateEquipmentFilter
} from 'features/ui';
import useEligibleConfigurations from 'hooks/use-eligible-configurations';
import useAirHandlerAvailabilityStatus from 'hooks/use-air-handler-availability-status';
import useExpectedFullFetchConfigIds from 'hooks/use-expected-full-fetch-config-ids';
import useHybridLeftoverSpaceIds from 'hooks/use-hybrid-leftover-space-ids';
import useIsPtacPathway from 'hooks/use-is-ptac-pathway';
import useMatchingAirHandlerMetrics from 'hooks/use-matching-air-handler-metrics';
import usePtacSystemsFetch from 'hooks/use-ptac-systems-fetch';
import useSnapWallMountedAirHandlers from 'hooks/use-snap-wall-mounted-air-handlers';
import useSpaceOpacity from 'hooks/use-space-opacity';
import { useStructureGrid } from 'hooks/useStructureGrid';
import {
  snapTransformToGridWithZoom,
  snapTransformToGridWithZoomAndOrigin
} from 'utils/grid-snap';

import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { getSpace3DGridPosition } from 'utils/3d';
import { condenserMatchingSystemCount } from 'utils/air-handler-pool-tuples';
import { getConfigurationSystemAvailability } from 'utils/configuration-system-availability';
import {
  condenserHasZoneCapacity,
  condenserZoneCapacityMessage,
  configurationZoneCapacityMessage
} from 'utils/condenser-zone-capacity';
import {
  selectDuctedDesignConfigurationIds,
  selectDuctedPairLinkMode
} from 'utils/ducted-design';
import { ductedDesignIncompleteReason } from 'utils/ducted-design-copy';
import {
  BoundingBox,
  computeAutoFitBoundingBox,
  computeFootprintBoundingBox,
  computeLargestLevelAutoFitBox,
  computeLinesetBoundingBox,
  unionBoundingBoxes
} from 'utils/auto-fit-bounding-box';
import {
  getAirHandlersFromConfig,
  getVisibleCondensers
} from 'utils/auto-lineset-assignment';
import { buildConcealedDuctLayoutsForConfiguration } from 'utils/concealed-duct-geometry';
import { isConfigurationComplete } from 'utils/configuration-eligibility';
import { resolveConfigurationEntryStepId } from 'utils/configuration-navigation';
import {
  NO_ELIGIBLE_CONFIGURATION_REASON,
  PTAC_DESIGN_ISSUES_REASON,
  SYSTEM_PREVIEW_MISSING_REASON
} from 'utils/configuration-step-reasons';
import { getConfigurationTabStatuses } from 'utils/configuration-step-summaries';
import {
  everyPositionedCondenserHasAirHandler,
  findCondenserById,
  findConfigurationById,
  findEmptyZone,
  getSectionSpaces,
  isConfigStepAccessible
} from 'utils/configurations';
import { hideSpaceContextMenu } from 'utils/context-menu-filters';
import {
  createHandleDragEnd,
  isCondenserOrAirHandlerDragType
} from 'utils/drag-and-drop';
import {
  buildConfigurationSelectionSignature,
  selectedConfigIdsToFilterValue
} from 'utils/equipment-config-selection';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import {
  findLevelForSection,
  getGridObjectsForLevel,
  getGridSpacesForLevel,
  getOverlayObjectsForLevel,
  getOverlaySpacesForLevel
} from 'utils/grid';
import { ptacUnitsPlacedSummary } from 'utils/ptac-configuration-sections';
import { hasErrorSeverityIssue } from 'utils/ptac-design-issues';
import { ptacRoomsTouchIn } from 'utils/ptac-room-adjacency';
import { ptacZoneAddRefusal } from 'utils/ptac-zone-click';
import {
  getConfigurationTabForStep,
  SidePanelTab
} from 'utils/side-panel-tabs';
import { findSpaceById, isLevelConditioned } from 'utils/structure';
import {
  confirmationModal,
  generateConfigurationSections,
  notification,
  warningModal
} from 'utils/ui';
import { getSystemOptions, getTotalValidSystems } from 'utils/valid-systems';
import { areExpectedFullFetchesSettled } from 'utils/valid-systems-settlement';
import { isErrorSeverity } from 'utils/validation';
import {
  findFirstUnzonedLevelUniqueId,
  selectCondenserForNewZone
} from 'utils/zone-condenser-selection';
import { placeZoneOutlineLabels } from 'utils/zone-label-placement';
import {
  calculateZoneTotalBTUs,
  generateZoneColor,
  getZoneSpaceShapes
} from 'utils/zone-outline';

export default function ConfigurationsPage() {
  const dispatch = useAppDispatch();
  useSnapWallMountedAirHandlers();
  usePtacSystemsFetch();
  const leftoverSpaceIds = useHybridLeftoverSpaceIds();
  const {
    configurations,
    activeConfig,
    protectedConfigurationIdsDuringGeneration
  } = useAppSelector((state) => state.configurations);
  const {
    selectedSystems,
    systemOptions,
    systemCombinationsValid,
    systemCombinationsFull
  } = useAppSelector((state) => state.systems);
  const { structure } = useAppSelector((state) => state.structure);
  const {
    easyMode,
    showZones,
    activeLevelIndex,
    activeTab,
    activeConfigurationStepId: currentSectionId,
    activeConfigurationStepConfigId,
    systemFilters,
    matchingAirHandlersMode,
    fetchingCondenserIds,
    fetchingFullConfigIds,
    failedValidSystemsConfigSources,
    equipmentConfigSelectionSignature,
    zoneDuctModes
  } = useAppSelector((state) => state.ui);
  const { projectSettings } = useAppSelector((state) => state.project);
  const { configGenerating } = useAppSelector((state) => state.ai);
  const { airHandlerProducts, condenserProducts } = useAppSelector(
    (state) => state.data
  );

  const activeConfiguration = configurations[activeConfig];
  const isPtacDesign = useIsPtacPathway() && activeConfiguration !== undefined;
  const ductedDesignIds = useAppSelector(selectDuctedDesignConfigurationIds);
  const isDuctedDesign =
    activeConfiguration !== undefined &&
    ductedDesignIds.has(activeConfiguration.uniqueId);
  const ductedPairLinkMode = useAppSelector(selectDuctedPairLinkMode);
  const designRuleResults = useAppSelector(
    (state) => state.configurations.designRuleResults
  );
  const hasPtacDesignErrors =
    isPtacDesign && hasErrorSeverityIssue(designRuleResults);
  const hasAirHandlers = useMemo(() => {
    if (!activeConfiguration) return false;
    return activeConfiguration.condensers.some((c) =>
      c.zones.some((z) => z.airHandler)
    );
  }, [activeConfiguration]);

  const allCondensersHaveAirHandler = useMemo(
    () => everyPositionedCondenserHasAirHandler(activeConfiguration),
    [activeConfiguration]
  );

  const validSystemCount = useMemo(() => {
    if (!activeConfiguration) return 0;
    return getTotalValidSystems(activeConfiguration);
  }, [activeConfiguration]);

  const matchingSystemCount = useMemo<number | null>(() => {
    if (!activeConfiguration) return null;
    if (!allCondensersHaveAirHandler) {
      return null;
    }
    const positioned = activeConfiguration.condensers.filter(
      (c) => c.position !== undefined
    );
    return positioned.reduce(
      (total, cond) => total + condenserMatchingSystemCount(cond),
      0
    );
  }, [activeConfiguration, allCondensersHaveAirHandler]);

  // The systems list modal reads the fetched rows, which only arrive once the
  // air_handlers step is complete and the full request has settled.
  const systemsListReady = useMemo(() => {
    if (!activeConfiguration) return false;
    const positioned = activeConfiguration.condensers.filter(
      (c) => c.position !== undefined
    );
    return (
      positioned.length > 0 &&
      positioned.every((c) => c.validSystemsFetched === true)
    );
  }, [activeConfiguration]);

  const isRefreshingValidSystems = useMemo(() => {
    if (!activeConfiguration || fetchingCondenserIds.length === 0) return false;
    const fetchingSet = new Set(fetchingCondenserIds);
    return activeConfiguration.condensers.some((c) =>
      fetchingSet.has(c.uniqueId)
    );
  }, [activeConfiguration, fetchingCondenserIds]);

  // Air handler drops are blocked only while availability is actually being
  // fetched; a failed options request (surfaced by AirHandlerList with a
  // retry) leaves the step usable.
  const airHandlerAvailabilityStatus = useAirHandlerAvailabilityStatus(
    activeConfiguration?.uniqueId
  );
  const isAwaitingAirHandlerAvailability =
    airHandlerAvailabilityStatus === 'loading';

  const priceRange = useMemo(() => {
    if (!activeConfiguration) return null;
    const condensersWithSystems = activeConfiguration.condensers.filter(
      (c) => c.validSystems.length > 0 || c.validDuctedSystems.length > 0
    );
    if (condensersWithSystems.length === 0) return null;
    let minPrice = 0;
    let maxPrice = 0;
    activeConfiguration.condensers.forEach((condenser) => {
      const ductlessPrices = condenser.validSystems.map(
        (s) => s.baseRetailPrice
      );
      const ductedPrices = condenser.validDuctedSystems.map((s) => s.price);
      const allPrices = [...ductlessPrices, ...ductedPrices];
      if (allPrices.length > 0) {
        minPrice += Math.min(...allPrices);
        maxPrice += Math.max(...allPrices);
      }
    });
    if (minPrice === 0 && maxPrice === 0) return null;
    return { min: minPrice, max: maxPrice };
  }, [activeConfiguration]);

  const activeConfigId = activeConfiguration?.uniqueId;
  const [systemsModalOpen, setSystemsModalOpen] = useState(false);
  const [ptacSystemsModalOpen, setPtacSystemsModalOpen] = useState(false);
  const [systemsModalMode, setSystemsModalMode] = useState<
    'matching' | 'valid'
  >('matching');
  const [showConfigSelectionModal, setShowConfigSelectionModal] =
    useState(false);

  // The `configurations` reference is intentionally NOT a dep: every
  // air-handler drop produces a new reference via markCondensersStale,
  // but the combination data the mapper consumes only updates after the
  // bulk-fetch watcher's debounce resolves. We instead depend on the
  // server-built combination maps and gate on `validSystemsFetched` to
  // skip the heavy mapper while the active config is mid-flight, then
  // coalesce bursts via a setTimeout so rapid drops collapse into a
  // single recompute.
  useEffect(() => {
    if (!activeConfigId) return undefined;
    const config = activeConfiguration;
    if (!config) return undefined;

    const positioned = config.condensers.filter(
      (c) => c.position !== undefined
    );
    if (positioned.length === 0) {
      const currentForActive =
        store.getState().systems.systemOptions[activeConfigId];
      if ((currentForActive?.length ?? 0) > 0) {
        dispatch(
          setSystemOptionsForConfig({ configId: activeConfigId, options: [] })
        );
      }
      return undefined;
    }

    // Skip while a recent air-handler drop has staled at least one
    // positioned condenser. The valid-systems watcher will refresh the
    // data within ~500ms; its dispatches re-trigger this effect with
    // `validSystemsFetched=true`.
    if (positioned.some((cond) => !cond.validSystemsFetched)) {
      return undefined;
    }

    const activeConfigRanked = positioned.every(
      (cond) => cond.validSystems.length > 0
    );
    if (!activeConfigRanked) {
      const currentForActive =
        store.getState().systems.systemOptions[activeConfigId];
      if ((currentForActive?.length ?? 0) > 0) {
        dispatch(
          setSystemOptionsForConfig({ configId: activeConfigId, options: [] })
        );
      }
      return undefined;
    }

    const handle = setTimeout(() => {
      const latestConfigs = store.getState().configurations.configurations;
      dispatch(setSystemOptions(getSystemOptions(latestConfigs)));
    }, 150);
    return () => clearTimeout(handle);
  }, [
    activeConfig,
    activeConfigId,
    activeConfiguration,
    airHandlerProducts,
    condenserProducts,
    systemFilters,
    matchingAirHandlersMode,
    projectSettings,
    systemCombinationsValid,
    systemCombinationsFull
  ]);
  const eligibleConfigurations = useEligibleConfigurations();
  const hasEligibleConfiguration = eligibleConfigurations.length > 0;

  const expectedFullFetchConfigIds = useExpectedFullFetchConfigIds();

  const allExpectedCombinationsSettled = useMemo(
    () =>
      areExpectedFullFetchesSettled({
        expectedConfigIds: expectedFullFetchConfigIds,
        systemCombinationsFull,
        failedValidSystemsConfigSources
      }),
    [
      expectedFullFetchConfigIds,
      systemCombinationsFull,
      failedValidSystemsConfigSources
    ]
  );

  const fetchingCompleteConfigIds = useMemo(
    () =>
      fetchingFullConfigIds.filter((id) =>
        isConfigurationComplete(
          configurations.find((config) => config.uniqueId === id),
          leftoverSpaceIds
        )
      ),
    [fetchingFullConfigIds, configurations, leftoverSpaceIds]
  );

  const waitingForValidCombinations =
    !hasEligibleConfiguration &&
    (!allExpectedCombinationsSettled || fetchingCompleteConfigIds.length > 0);

  const equipmentResetRef = useRef(false);

  const {
    activeDragData,
    setActiveDragData,
    activeId,
    setActiveId,
    draggedSectionSpaces,
    setDraggedSectionSpaces,
    show3DView,
    setShow3DView,
    gridZoom,
    setGridZoom,
    gridPanOffset,
    setGridPanOffset
  } = useStructureGrid();
  const [gridLinesOpacity, setGridLinesOpacity] = useState(50);
  const [overlaySpacesOpacity, setOverlaySpacesOpacity] = useState(50);
  const [inactiveLevelOpacity, setInactiveLevelOpacity] = useState(30);
  const [colorLinesetsByCondenser, setColorLinesetsByCondenser] =
    useState(true);
  const [spaceOpacity, setSpaceOpacity] = useSpaceOpacity();
  const structureGridRef = useRef<HTMLDivElement>(null);
  const [activeZoneId, setActiveZoneId] = useState<string | null>(null);
  const [hoveredZoneId, setHoveredZoneId] = useState<string | null>(null);
  const levels = useMemo(() => [...structure], [structure]);
  const selectedLevelIndex = activeLevelIndex;
  const effectiveFootprintPolygons = useMemo(
    () => getEffectiveFootprintPolygons(levels[selectedLevelIndex]),
    [levels, selectedLevelIndex]
  );
  const setCurrentSectionId = useCallback(
    (id: string) => dispatch(setActiveConfigurationStepId(id)),
    [dispatch]
  );
  const [activePanelTab, setActivePanelTab] = useState('zones');

  const { getZoneValidation, issues: filteredDesignRuleResults } =
    useConfigurationValidation(currentSectionId);

  const { metrics, isLoading: metricsLoading } = useMatchingAirHandlerMetrics();

  const structureSignatureForConfigurationSections = useMemo(
    () =>
      JSON.stringify(
        structure.map((level) => ({
          id: level.uniqueId,
          spaces: level.sections.flatMap((section) =>
            section.spaces.map((space) => ({
              id: space.uniqueId,
              conditioned: space.conditioned
            }))
          )
        }))
      ),
    [structure]
  );

  // Cheap structural fingerprint of the bits `generateConfigurationSections`
  // depends on. Replaces the previous inline JSON.stringify in the deps
  // array, which ran on every render regardless.
  const activeConfigSectionsSignature = useMemo(() => {
    const config = configurations[activeConfig];
    if (!config) return 'null';
    const parts: string[] = [];
    config.condensers.forEach((c) => {
      parts.push(c.uniqueId);
      parts.push(c.position ? `${c.position.x},${c.position.y}` : '_');
      c.zones.forEach((z) => {
        parts.push(`${z.zoneItems.length}:${z.airHandler ? '1' : '0'}`);
      });
      parts.push('|');
    });
    parts.push(';');
    (config.lineSets ?? []).forEach((ls) => {
      parts.push(`${ls.uniqueId}:${ls.airHandlerUniqueId ?? ''}`);
    });
    // The air_handlers step is also gated on the options pool, which arrives
    // after the geometry above has settled.
    parts.push(`;availability=${getConfigurationSystemAvailability(config)}`);
    return parts.join(',');
  }, [activeConfig, configurations]);

  const systemOptionsConfigIdsSignature = useMemo(
    () => Object.keys(systemOptions).sort().join(','),
    [systemOptions]
  );

  const configurationSections = useMemo(() => {
    return generateConfigurationSections(activeConfig);
  }, [
    activeConfig,
    selectedSystems.length,
    hasEligibleConfiguration,
    structureSignatureForConfigurationSections,
    activeConfigSectionsSignature,
    systemOptionsConfigIdsSignature
  ]);

  const unzonedSpaceCount = useMemo(() => {
    if (!activeConfiguration) return 0;
    const allZonedSpaceIds = new Set(
      activeConfiguration.condensers
        .flatMap((c) => c.zones)
        .flatMap((z) => z.zoneItems.map((item) => item.structureItemId))
    );
    let count = 0;
    for (const level of structure) {
      for (const section of level.sections) {
        for (const space of section.spaces) {
          if (
            space.conditioned !== false &&
            !allZonedSpaceIds.has(space.uniqueId)
          ) {
            count += 1;
          }
        }
      }
    }
    return count;
  }, [activeConfiguration, structure]);

  const hasUnzonedSpaces = unzonedSpaceCount > 0;

  const [editingConfigurationUniqueId, setEditingConfigurationUniqueId] =
    useState<string | null>(null);

  if (
    editingConfigurationUniqueId !== null &&
    editingConfigurationUniqueId !== activeConfiguration?.uniqueId
  ) {
    setEditingConfigurationUniqueId(null);
  }

  const isViewOnlyConfiguration =
    Boolean(activeConfiguration?.aiGenerated) &&
    editingConfigurationUniqueId !== activeConfiguration?.uniqueId;

  const handleEditActiveConfiguration = useCallback(() => {
    if (!activeConfiguration) return;
    setEditingConfigurationUniqueId(activeConfiguration.uniqueId);
  }, [activeConfiguration]);

  useEffect(() => {
    if (!configGenerating || !editingConfigurationUniqueId) return;
    if (
      protectedConfigurationIdsDuringGeneration.includes(
        editingConfigurationUniqueId
      )
    ) {
      return;
    }
    dispatch(
      addProtectedConfigurationIdDuringGeneration(editingConfigurationUniqueId)
    );
  }, [
    configGenerating,
    editingConfigurationUniqueId,
    protectedConfigurationIdsDuringGeneration,
    dispatch
  ]);

  const handleSelectConfiguration = useCallback(
    (configurationIndex: number) => {
      if (!configurations[configurationIndex]) return;
      dispatch(setActiveConfig(configurationIndex));
    },
    [configurations, dispatch]
  );

  useEffect(() => {
    if (!activeConfiguration) return;

    const allZones = activeConfiguration.condensers.flatMap((c) => c.zones);
    const activeZoneExists = allZones.some((z) => z.uniqueId === activeZoneId);

    if (!activeZoneExists && allZones.length > 0) {
      setActiveZoneId(allZones[0].uniqueId);
    }
  }, [activeConfiguration, activeZoneId]);

  const panelWidth = 360;

  const getGridSpacesForLevelCallback = useCallback(
    (levelIndex: number) => {
      return getGridSpacesForLevel(levels, levelIndex);
    },
    [levels]
  );

  const concealedDuctLayoutMap = useMemo(
    () =>
      buildConcealedDuctLayoutsForConfiguration(
        configurations[activeConfig],
        levels,
        getGridSpacesForLevelCallback,
        zoneDuctModes
      ),
    [
      configurations,
      activeConfig,
      levels,
      getGridSpacesForLevelCallback,
      zoneDuctModes
    ]
  );

  const concealedDuctLayouts = useMemo(
    () => Array.from(concealedDuctLayoutMap.values()),
    [concealedDuctLayoutMap]
  );

  const getGridObjectsForLevelCallback = useCallback(
    (levelIndex: number): GridObjectType[] => {
      return getGridObjectsForLevel(levels, levelIndex, {
        configurations,
        activeConfigIndex: activeConfig,
        concealedDuctLayouts: concealedDuctLayoutMap,
        ptacDesign: isPtacDesign
      });
    },
    [levels, configurations, activeConfig, concealedDuctLayoutMap, isPtacDesign]
  );

  useEffect(() => {
    if (levels.length > 0 && selectedLevelIndex >= levels.length) {
      dispatch(setActiveLevelIndex(levels.length - 1));
    }
  }, [dispatch, levels.length, selectedLevelIndex]);

  const isOnZoneStep = currentSectionId.startsWith('add_spaces_zone_');
  const prevIsOnZoneStepRef = useRef(false);
  const prevZoneStepIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOnZoneStep || !activeConfiguration) {
      prevIsOnZoneStepRef.current = isOnZoneStep;
      prevZoneStepIdRef.current = null;
      return;
    }

    const allZonedSpaceIds = new Set(
      activeConfiguration.condensers
        .flatMap((condenser) => condenser.zones)
        .flatMap((zone) => zone.zoneItems.map((item) => item.structureItemId))
    );

    const findFirstLevelWithUnzonedSpaces = () => {
      return structure.findIndex((level) =>
        level.sections.some((section) =>
          section.spaces.some(
            (space) =>
              space.conditioned !== false &&
              !allZonedSpaceIds.has(space.uniqueId)
          )
        )
      );
    };

    const currentLevelHasUnzonedSpaces = () => {
      const currentLevel = structure[activeLevelIndex];
      if (!currentLevel) return false;
      return currentLevel.sections.some((section) => {
        return section.spaces.some((space) => {
          const isConditioned = space.conditioned !== false;
          const isUnzoned = !allZonedSpaceIds.has(space.uniqueId);
          return isConditioned && isUnzoned;
        });
      });
    };

    const justEnteredZoneStep = !prevIsOnZoneStepRef.current && isOnZoneStep;
    const movedToNewZoneStep =
      prevZoneStepIdRef.current !== null &&
      prevZoneStepIdRef.current !== currentSectionId;

    if (justEnteredZoneStep) {
      const firstConditionedIndex = structure.findIndex((level) =>
        isLevelConditioned(level)
      );
      dispatch(
        setActiveLevelIndex(
          firstConditionedIndex !== -1 ? firstConditionedIndex : 0
        )
      );
    } else if (movedToNewZoneStep && !currentLevelHasUnzonedSpaces()) {
      const nextLevelWithUnzoned = findFirstLevelWithUnzonedSpaces();
      if (nextLevelWithUnzoned !== -1) {
        dispatch(setActiveLevelIndex(nextLevelWithUnzoned));
      }
    }

    prevIsOnZoneStepRef.current = isOnZoneStep;
    prevZoneStepIdRef.current = currentSectionId;
  }, [
    isOnZoneStep,
    currentSectionId,
    activeConfiguration,
    structure,
    activeLevelIndex,
    dispatch
  ]);

  const prevSectionIdRef = useRef<string | null>(null);

  useEffect(() => {
    const justEnteredCondensers =
      prevSectionIdRef.current !== 'condensers' &&
      currentSectionId === 'condensers';

    if (justEnteredCondensers) {
      const lowestConditionedNonBasementIndex = structure.findIndex(
        (level) => level.label !== 'Basement' && isLevelConditioned(level)
      );
      if (lowestConditionedNonBasementIndex !== -1) {
        dispatch(setActiveLevelIndex(lowestConditionedNonBasementIndex));
      }
    }

    prevSectionIdRef.current = currentSectionId;
  }, [currentSectionId, structure, dispatch]);

  const stepsWithZoneVisualsEnabled =
    isOnZoneStep ||
    ['condensers', 'air_handlers', 'line_sets'].includes(currentSectionId);

  const generateZoneOutlines = useCallback(
    (gridSpaces: GridSpaceDataType[]): ZoneOutlineData[] => {
      if (!stepsWithZoneVisualsEnabled) return [];
      if (!activeConfiguration) return [];

      const allZones = activeConfiguration.condensers.flatMap((c) => c.zones);
      const zoneIdToGlobalIndex = new Map(
        allZones.map((zone, i) => [zone.uniqueId, i])
      );

      const outlines: ZoneOutlineData[] = [];
      activeConfiguration.condensers.forEach((condenser, condenserIndex) => {
        condenser.zones.forEach((zone) => {
          if (zone.zoneItems.length === 0) return;

          const spaceShapes = getZoneSpaceShapes(zone, gridSpaces);
          if (spaceShapes.length === 0) return;

          const colorIndex = zoneIdToGlobalIndex.get(zone.uniqueId) ?? 0;
          const color = generateZoneColor(colorIndex);
          const BTUs = calculateZoneTotalBTUs(zone);

          outlines.push({
            zoneId: zone.uniqueId,
            zoneName: zone.name,
            condenserIndex: condenserIndex + 1,
            BTUs,
            spacePositions: spaceShapes.map((s) => s.position),
            spaceShapes,
            color
          });
        });
      });

      return outlines;
    },
    [activeConfiguration, stepsWithZoneVisualsEnabled]
  );

  const allObjects3D = useMemo(() => {
    const objects: ReturnType<typeof getGridObjectsForLevel> = [];
    for (let levelIndex = 0; levelIndex < structure.length; levelIndex += 1) {
      const levelObjects = getGridObjectsForLevel(structure, levelIndex, {
        configurations,
        activeConfigIndex: activeConfig,
        concealedDuctLayouts: concealedDuctLayoutMap,
        ptacDesign: isPtacDesign
      });
      objects.push(...levelObjects);
    }
    return objects;
  }, [
    structure,
    configurations,
    activeConfig,
    concealedDuctLayoutMap,
    isPtacDesign
  ]);

  const sectionColors3D = useMemo(() => {
    const colorMap = new Map<
      string,
      { bg: string; border: string; text: string }
    >();

    if (!showZones) return colorMap;

    const baseColors = [
      { hue: 0, saturation: 75, lightness: 65 },
      { hue: 30, saturation: 75, lightness: 65 },
      { hue: 60, saturation: 75, lightness: 65 },
      { hue: 120, saturation: 75, lightness: 65 },
      { hue: 270, saturation: 75, lightness: 65 },
      { hue: 180, saturation: 75, lightness: 65 },
      { hue: 210, saturation: 75, lightness: 65 },
      { hue: 240, saturation: 75, lightness: 65 },
      { hue: 300, saturation: 75, lightness: 65 },
      { hue: 330, saturation: 75, lightness: 65 },
      { hue: 15, saturation: 75, lightness: 65 },
      { hue: 45, saturation: 75, lightness: 65 },
      { hue: 90, saturation: 75, lightness: 65 },
      { hue: 150, saturation: 75, lightness: 65 },
      { hue: 200, saturation: 75, lightness: 65 }
    ];

    const uniqueSections: Array<{ uniqueId: string }> = [];
    const seenIds = new Set<string>();

    structure.forEach((level) => {
      level.sections.forEach((section) => {
        if (!seenIds.has(section.uniqueId)) {
          seenIds.add(section.uniqueId);
          uniqueSections.push({ uniqueId: section.uniqueId });
        }
      });
    });

    uniqueSections.sort((a, b) => a.uniqueId.localeCompare(b.uniqueId));

    const generateUniqueColor = (index: number) => {
      if (index < baseColors.length) {
        return baseColors[index];
      }

      const hueStep = 360 / Math.max(uniqueSections.length, baseColors.length);
      const hue = (index * hueStep) % 360;
      const saturation = 70 + (index % 3) * 5;
      const lightness = 60 + (index % 4) * 3;

      return {
        hue: Math.round(hue),
        saturation: Math.max(65, Math.min(80, saturation)),
        lightness: Math.max(60, Math.min(72, lightness))
      };
    };

    uniqueSections.forEach((section, index) => {
      const color = generateUniqueColor(index);
      colorMap.set(section.uniqueId, {
        bg: `hsl(${color.hue}, ${color.saturation}%, ${color.lightness}%)`,
        border: `hsl(${color.hue}, ${color.saturation}%, ${
          color.lightness - 15
        }%)`,
        text: `hsl(${color.hue}, ${color.saturation}%, ${
          color.lightness - 30
        }%)`
      });
    });

    return colorMap;
  }, [structure, showZones]);

  const spaces3D = useMemo(() => {
    return structure.flatMap((level) =>
      level.sections.flatMap((section) =>
        section.spaces.map((space) => ({
          id: space.uniqueId,
          spaceId: space.uniqueId,
          label: space.label,
          position: getSpace3DGridPosition(space),
          vertices: space.vertices,
          type: 'space' as const,
          itemId: space.itemId,
          sectionId: section.uniqueId,
          spacesInSection: section.spaces.length,
          sectionColor: sectionColors3D.get(section.uniqueId)
        }))
      )
    );
  }, [structure, sectionColors3D]);

  const zoneOutlinesForLevel = useMemo(() => {
    const gridSpaces = getGridSpacesForLevelCallback(selectedLevelIndex);
    return generateZoneOutlines(gridSpaces);
  }, [getGridSpacesForLevelCallback, selectedLevelIndex, generateZoneOutlines]);

  const zoneOutlinesWithLabels = useMemo(() => {
    if (zoneOutlinesForLevel.length === 0) return zoneOutlinesForLevel;

    return placeZoneOutlineLabels(
      zoneOutlinesForLevel,
      getGridSpacesForLevelCallback(selectedLevelIndex),
      getGridObjectsForLevelCallback(selectedLevelIndex),
      configurations[activeConfig]?.lineSets || [],
      levels[selectedLevelIndex]?.uniqueId
    );
  }, [
    zoneOutlinesForLevel,
    getGridSpacesForLevelCallback,
    getGridObjectsForLevelCallback,
    selectedLevelIndex,
    configurations,
    activeConfig,
    levels
  ]);

  const getOverlaySpacesForLevelCallback = useCallback(
    (levelIndex: number) => {
      return getOverlaySpacesForLevel(levels, levelIndex);
    },
    [levels]
  );

  const getOverlayObjectsForLevelCallback = useCallback(
    (levelIndex: number) => {
      return getOverlayObjectsForLevel(levels, levelIndex, {
        configurations,
        activeConfigIndex: activeConfig,
        concealedDuctLayouts: concealedDuctLayoutMap,
        ptacDesign: isPtacDesign
      });
    },
    [levels, configurations, activeConfig, concealedDuctLayoutMap, isPtacDesign]
  );

  const unifiedAutoFitBoundingBox = useMemo(() => {
    if (levels.length === 0) return null;
    const lineSets = configurations[activeConfig]?.lineSets || [];
    const perLevelContent: (BoundingBox | null)[] = [];
    const perLevelCenter: (BoundingBox | null)[] = [];
    levels.forEach((level, levelIndex) => {
      const footprintBox = computeFootprintBoundingBox(
        getEffectiveFootprintPolygons(level)
      );
      const gridSpaces = getGridSpacesForLevel(levels, levelIndex);
      const objects = getGridObjectsForLevel(levels, levelIndex, {
        configurations,
        activeConfigIndex: activeConfig,
        ptacDesign: isPtacDesign
      });
      const labeledOutlines = placeZoneOutlineLabels(
        generateZoneOutlines(gridSpaces),
        gridSpaces,
        objects,
        lineSets,
        level.uniqueId
      );
      const layoutBox =
        footprintBox ?? computeAutoFitBoundingBox(gridSpaces, [], []);
      const contentBox = computeAutoFitBoundingBox(
        footprintBox ? [] : gridSpaces,
        objects,
        labeledOutlines
      );
      const linesetBox = computeLinesetBoundingBox(lineSets, level.uniqueId);
      perLevelContent.push(
        unionBoundingBoxes([layoutBox, contentBox, linesetBox])
      );
      perLevelCenter.push(layoutBox);
    });
    return computeLargestLevelAutoFitBox(perLevelContent, perLevelCenter);
  }, [
    levels,
    configurations,
    activeConfig,
    generateZoneOutlines,
    isPtacDesign
  ]);

  const snapModifier: Modifier = useMemo(
    () =>
      ({ transform, activeNodeRect }) => {
        const gridRect = structureGridRef.current?.getBoundingClientRect();
        if (!gridRect || !activeNodeRect) {
          return snapTransformToGridWithZoom(
            transform,
            STRUCTURE_GRID_SIZE,
            gridZoom
          );
        }

        return snapTransformToGridWithZoomAndOrigin({
          transform,
          activeNodeRect,
          gridSize: STRUCTURE_GRID_SIZE,
          zoom: gridZoom,
          gridOrigin: {
            x: gridRect.left + gridPanOffset.x,
            y: gridRect.top + gridPanOffset.y
          }
        });
      },
    [gridZoom, gridPanOffset.x, gridPanOffset.y]
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5
      }
    })
  );

  const withEquipmentResetConfirmation = useCallback(
    (action: () => void) => {
      if (hasAirHandlers && !equipmentResetRef.current) {
        confirmationModal(
          'Editing zones will remove all placed air handlers from this configuration. Do you want to continue?',
          async () => {
            if (activeConfiguration) {
              await dispatch(
                resetConfigurationEquipment({
                  configurationId: activeConfiguration.uniqueId
                })
              );
            }
            equipmentResetRef.current = true;
            action();
          },
          'Reset Equipment?'
        );
      } else {
        action();
      }
    },
    [hasAirHandlers, activeConfiguration, dispatch]
  );

  const createNextZone = useCallback(
    async (
      spaceLevelUniqueId: string | null,
      clickedSpacePosition: { x: number; y: number } | null
    ): Promise<ZoneRefType | null> => {
      if (!activeConfiguration) return null;

      if (isPtacDesign) {
        return dispatch(
          createPtacZone({ configurationId: activeConfiguration.uniqueId })
        ).unwrap();
      }

      const chosenCondenser = selectCondenserForNewZone(
        activeConfiguration.condensers,
        spaceLevelUniqueId,
        clickedSpacePosition
      );
      if (!chosenCondenser) {
        const hasPlacedCondenser = activeConfiguration.condensers.some(
          (c) => c.position !== undefined
        );
        if (hasPlacedCondenser) {
          notification(
            configurationZoneCapacityMessage(activeConfiguration),
            'warning'
          );
        }
        return null;
      }

      const zoneId = await dispatch(
        createZoneOnCondenser({
          configurationId: activeConfiguration.uniqueId,
          condenserId: chosenCondenser.uniqueId
        })
      ).unwrap();
      return zoneId ? { zoneId, condenserId: chosenCondenser.uniqueId } : null;
    },
    [activeConfiguration, isPtacDesign, dispatch]
  );

  const handleZoneSpaceClick = useCallback(
    (spaceId: string, sectionId: string) => {
      if (!activeConfiguration || isViewOnlyConfiguration) return;

      let existingZone: ZoneRefType | null = null;
      for (const condenser of activeConfiguration.condensers) {
        for (const zone of condenser.zones) {
          const hasSpace = zone.zoneItems.some(
            (item) => item.structureItemId === spaceId
          );
          if (hasSpace) {
            existingZone = {
              zoneId: zone.uniqueId,
              condenserId: condenser.uniqueId
            };
            break;
          }
        }
        if (existingZone) break;
      }

      if (existingZone) {
        const performRemove = () => {
          dispatch(
            removeSpaceFromZone({
              configurationId: activeConfiguration.uniqueId,
              condenserId: existingZone!.condenserId,
              zoneId: existingZone!.zoneId,
              spaceId
            })
          );
        };
        withEquipmentResetConfirmation(performRemove);
      } else {
        const performAdd = async () => {
          const sectionSpaces = getSectionSpaces(structure, sectionId);
          if (sectionSpaces.length === 0) return;

          let targetZoneId: string | undefined;
          let targetCondenserId: string | undefined;

          for (const condenser of activeConfiguration.condensers) {
            const zone = condenser.zones.find(
              (z) => z.uniqueId === activeZoneId
            );
            if (zone) {
              targetZoneId = zone.uniqueId;
              targetCondenserId = condenser.uniqueId;
              break;
            }
          }

          if (targetZoneId && isPtacDesign) {
            const refusal = ptacZoneAddRefusal(
              activeConfiguration,
              targetZoneId,
              spaceId,
              ptacRoomsTouchIn(structure)
            );
            if (refusal) {
              warningModal(refusal);
              return;
            }
          }

          if (!targetZoneId) {
            const clickedSpace = findSpaceById(structure, spaceId);
            const spaceLevelUniqueId =
              findLevelForSection(sectionId, structure)?.uniqueId ?? null;
            const created = await createNextZone(
              spaceLevelUniqueId,
              clickedSpace?.position ?? null
            );
            if (!created) return;

            targetZoneId = created.zoneId;
            targetCondenserId = created.condenserId;
            setActiveZoneId(created.zoneId);
          }

          if (!targetCondenserId) return;

          dispatch(
            addSpacesToZone({
              configurationId: activeConfiguration.uniqueId,
              condenserId: targetCondenserId,
              zoneId: targetZoneId,
              spaces: sectionSpaces.map((space) => ({
                structureItemId: space.uniqueId,
                name: space.label,
                BTUs: space.BTUs
              }))
            })
          );
        };
        withEquipmentResetConfirmation(performAdd);
      }
    },
    [
      activeConfiguration,
      isViewOnlyConfiguration,
      isPtacDesign,
      createNextZone,
      structure,
      activeZoneId,
      dispatch,
      withEquipmentResetConfirmation
    ]
  );

  const focusZoneStep = useCallback(
    (zoneId: string) => {
      setActiveZoneId(zoneId);
      const freshSections = generateConfigurationSections(
        store.getState().configurations.activeConfig
      );
      const matchingStep = freshSections.find(
        (s) =>
          s.id.startsWith('add_spaces_zone_') &&
          s.steps?.[0]?.zoneUniqueId === zoneId
      );
      if (matchingStep) {
        setCurrentSectionId(matchingStep.id);
      }
    },
    [setCurrentSectionId]
  );

  const focusEmptyOrNewZone = useCallback(
    async (
      zones: ZoneType[],
      createZone: () => Promise<string | null>
    ): Promise<void> => {
      const zoneId = findEmptyZone(zones)?.uniqueId ?? (await createZone());
      if (zoneId) {
        focusZoneStep(zoneId);
      }
    },
    [focusZoneStep]
  );

  const freshActiveConfiguration = useCallback(
    () =>
      activeConfiguration
        ? findConfigurationById(
            store.getState().configurations.configurations,
            activeConfiguration.uniqueId
          )
        : undefined,
    [activeConfiguration]
  );

  const handleAddPtacZone = useCallback(() => {
    const freshConfig = freshActiveConfiguration();
    if (!freshConfig) return;

    focusEmptyOrNewZone(
      freshConfig.condensers.flatMap((condenser) => condenser.zones),
      async () =>
        (
          await dispatch(
            createPtacZone({ configurationId: freshConfig.uniqueId })
          ).unwrap()
        )?.zoneId ?? null
    );
  }, [freshActiveConfiguration, focusEmptyOrNewZone, dispatch]);

  const handleAddZoneToCondenser = useCallback(
    (condenserId: string) => {
      const freshConfig = freshActiveConfiguration();
      const condenser = freshConfig
        ? findCondenserById(freshConfig, condenserId)
        : undefined;
      if (!freshConfig || !condenser) return;

      focusEmptyOrNewZone(condenser.zones, async () => {
        if (!condenserHasZoneCapacity(condenser)) {
          notification(condenserZoneCapacityMessage(condenser), 'warning');
          return null;
        }
        return dispatch(
          createZoneOnCondenser({
            configurationId: freshConfig.uniqueId,
            condenserId
          })
        ).unwrap();
      });
    },
    [freshActiveConfiguration, focusEmptyOrNewZone, dispatch]
  );

  const handleRemoveSpaceFromZone = useCallback(
    (spaceId: string, zoneId: string, condenserId: string) => {
      if (!activeConfiguration) return;

      const performAction = () => {
        dispatch(
          removeSpaceFromZone({
            configurationId: activeConfiguration.uniqueId,
            condenserId,
            zoneId,
            spaceId
          })
        );
      };

      withEquipmentResetConfirmation(performAction);
    },
    [activeConfiguration, dispatch, withEquipmentResetConfirmation]
  );

  const handleDeleteZone = useCallback(
    (zoneId: string, condenserId: string) => {
      if (!activeConfiguration) return;

      const performAction = () => {
        dispatch(
          deleteZone({
            configurationId: activeConfiguration.uniqueId,
            condenserId,
            zoneId
          })
        );
      };

      withEquipmentResetConfirmation(performAction);
    },
    [activeConfiguration, dispatch, withEquipmentResetConfirmation]
  );

  const handleDeleteEmptyZonesOnLeave = useCallback(() => {
    if (isViewOnlyConfiguration) return;
    if (
      currentSectionId.startsWith('add_spaces_zone_') &&
      activeConfiguration
    ) {
      dispatch(
        deleteEmptyZones({ configurationId: activeConfiguration.uniqueId })
      );
    }
  }, [
    currentSectionId,
    activeConfiguration,
    isViewOnlyConfiguration,
    dispatch
  ]);

  const handleZoneSelect = useCallback(
    (zoneId: string) => {
      setActiveZoneId(zoneId);
      const matchingStep = configurationSections.find(
        (s) =>
          s.id.startsWith('add_spaces_zone_') &&
          s.steps?.[0]?.zoneUniqueId === zoneId
      );
      if (matchingStep) {
        setCurrentSectionId(matchingStep.id);
      }
    },
    [configurationSections]
  );

  const hasAnyZonedSpaces = useMemo(() => {
    if (!activeConfiguration) return false;
    return activeConfiguration.condensers.some((c) =>
      c.zones.some((z) => z.zoneItems.length > 0)
    );
  }, [activeConfiguration]);

  const currentZoneHasSpaces = useMemo(() => {
    if (!activeConfiguration || !activeZoneId) return false;
    const allZones = activeConfiguration.condensers.flatMap((c) => c.zones);
    const zone = allZones.find((z) => z.uniqueId === activeZoneId);
    return zone ? zone.zoneItems.length > 0 : false;
  }, [activeConfiguration, activeZoneId]);

  const isLastZoneStep = useMemo(() => {
    if (!isOnZoneStep) return false;
    const currentStepIndex = configurationSections.findIndex(
      (s) => s.id === currentSectionId
    );
    const nextStep = configurationSections[currentStepIndex + 1];
    return !nextStep || !nextStep.id.startsWith('add_spaces_zone_');
  }, [isOnZoneStep, configurationSections, currentSectionId]);

  const handleNextStep = useCallback((): boolean | void => {
    if (!activeConfiguration) return undefined;

    const isLastConfigStep =
      configurationSections[configurationSections.length - 1]?.id ===
      currentSectionId;
    if (
      !isPtacDesign &&
      (isLastConfigStep || isViewOnlyConfiguration) &&
      eligibleConfigurations.length >= 2 &&
      buildConfigurationSelectionSignature(eligibleConfigurations) !==
        equipmentConfigSelectionSignature
    ) {
      handleDeleteEmptyZonesOnLeave();
      setShowConfigSelectionModal(true);
      return false;
    }

    if (isViewOnlyConfiguration) return undefined;

    if (currentSectionId === 'condensers' && hasAirHandlers) {
      dispatch(
        deleteEmptyZones({ configurationId: activeConfiguration.uniqueId })
      );
      return undefined;
    }

    if (currentSectionId === 'condensers' && hasUnzonedSpaces) {
      const placedCondensers = activeConfiguration.condensers.filter(
        (c) => c.position !== undefined
      );
      if (placedCondensers.length === 0) return undefined;

      const existingEmptyZone = activeConfiguration.condensers
        .flatMap((c) => c.zones)
        .find((z) => z.zoneItems.length === 0);

      if (existingEmptyZone) {
        setCurrentSectionId('add_spaces_zone_1');
        setActiveZoneId(existingEmptyZone.uniqueId);
        return false;
      }

      const targetCondenser = selectCondenserForNewZone(
        activeConfiguration.condensers,
        findFirstUnzonedLevelUniqueId(structure, activeConfiguration),
        null
      );
      if (!targetCondenser) {
        notification(
          configurationZoneCapacityMessage(activeConfiguration),
          'warning'
        );
        return false;
      }

      dispatch(
        createZoneOnCondenser({
          configurationId: activeConfiguration.uniqueId,
          condenserId: targetCondenser.uniqueId
        })
      )
        .unwrap()
        .then((newZoneId) => {
          if (newZoneId) {
            setCurrentSectionId('add_spaces_zone_1');
            setActiveZoneId(newZoneId);
          }
        });
      return false;
    }

    const isZoneStep = currentSectionId.startsWith('add_spaces_zone_');
    if (!isZoneStep) {
      handleDeleteEmptyZonesOnLeave();
      return undefined;
    }

    const currentStepIndex = configurationSections.findIndex(
      (s) => s.id === currentSectionId
    );
    const nextStep = configurationSections[currentStepIndex + 1];

    if (nextStep && nextStep.id.startsWith('add_spaces_zone_')) {
      const nextZoneId = nextStep.steps?.[0]?.zoneUniqueId;
      setCurrentSectionId(nextStep.id);
      setActiveZoneId(nextZoneId ?? null);
      return false;
    }

    if (hasAirHandlers || !hasUnzonedSpaces) {
      dispatch(
        deleteEmptyZones({ configurationId: activeConfiguration.uniqueId })
      );
      setCurrentSectionId('air_handlers');
      return false;
    }

    const placedCondensers = activeConfiguration.condensers.filter(
      (c) => c.position !== undefined
    );
    if (!isPtacDesign && placedCondensers.length === 0) return undefined;

    createNextZone(
      findFirstUnzonedLevelUniqueId(structure, activeConfiguration),
      null
    ).then((created) => {
      if (created) {
        const allZonesWithItems = activeConfiguration.condensers
          .flatMap((c) => c.zones)
          .filter((z) => z.zoneItems.length > 0);
        const nextZoneNumber = allZonesWithItems.length + 1;
        setCurrentSectionId(`add_spaces_zone_${nextZoneNumber}`);
        setActiveZoneId(created.zoneId);
      }
    });

    return false;
  }, [
    activeConfiguration,
    isViewOnlyConfiguration,
    isPtacDesign,
    createNextZone,
    currentSectionId,
    configurationSections,
    eligibleConfigurations,
    hasUnzonedSpaces,
    hasAnyZonedSpaces,
    hasAirHandlers,
    structure,
    dispatch,
    setCurrentSectionId,
    handleDeleteEmptyZonesOnLeave,
    selectedSystems,
    equipmentConfigSelectionSignature
  ]);

  const handleConfirmConfigSelection = useCallback(
    (selectedConfigIds: string[]) => {
      const eligibleConfigIds = eligibleConfigurations.map(
        (config) => config.uniqueId
      );
      dispatch(
        updateEquipmentFilter({
          configIds: selectedConfigIdsToFilterValue(
            selectedConfigIds,
            eligibleConfigIds
          )
        })
      );
      dispatch(
        setEquipmentConfigSelectionSignature(
          buildConfigurationSelectionSignature(eligibleConfigurations)
        )
      );
      setShowConfigSelectionModal(false);
      dispatch(setActiveTab('chooseSystems'));
    },
    [eligibleConfigurations, dispatch]
  );

  const handleNextAllZonesAssigned = useCallback(() => {
    if (!activeConfiguration) return;
    dispatch(
      deleteEmptyZones({ configurationId: activeConfiguration.uniqueId })
    );
    setCurrentSectionId('air_handlers');
  }, [activeConfiguration, dispatch, setCurrentSectionId]);

  const panelContentMap: Record<string, ReactNode> = useMemo(
    () => ({
      zones: (
        <div className='flex flex-col gap-4 p-3'>
          <ZoneCondenserMap
            activeZoneId={activeZoneId}
            isOnZoneStep={isOnZoneStep}
            variant={isPtacDesign ? 'flat' : 'condensers'}
            onSetActiveZone={handleZoneSelect}
            onRemoveSpaceFromZone={handleRemoveSpaceFromZone}
            onDeleteZone={handleDeleteZone}
            onAddZoneToCondenser={handleAddZoneToCondenser}
            onAddZone={handleAddPtacZone}
          />
        </div>
      ),
      condensers: (
        <div className='flex flex-col gap-4 p-3'>
          <CondenserList />
        </div>
      ),
      air_handlers: (
        <div className='flex flex-col gap-4 p-3'>
          {isPtacDesign ? (
            <PtacUnitsPanel readOnly={isViewOnlyConfiguration} />
          ) : (
            <AirHandlerList />
          )}
        </div>
      ),
      line_sets: (
        <div className='flex flex-col gap-4 p-3'>
          <div className='text-center text-sm text-gray-500'>
            Right click on a condenser or air handler to generate a line set.
          </div>
          <LinesetSummaryPanel />
        </div>
      )
    }),
    [
      handleAddZoneToCondenser,
      handleAddPtacZone,
      activeZoneId,
      isOnZoneStep,
      handleRemoveSpaceFromZone,
      handleDeleteZone,
      handleZoneSelect,
      isAwaitingAirHandlerAvailability,
      isViewOnlyConfiguration,
      isPtacDesign
    ]
  );

  const sidebarSections: SidebarSectionType[] = useMemo(
    () =>
      configurationSections.map((step) => ({
        ...step,
        fullWidthContent: false,
        renderPanelContent: () => panelContentMap[step.id] ?? null
      })),
    [configurationSections, panelContentMap]
  );

  const tabDisabled = useMemo(() => {
    const result: Record<string, boolean> = {};
    (['condensers', 'zones', 'air_handlers', 'line_sets'] as const).forEach(
      (tabId) => {
        const firstSection = configurationSections.find(
          (s) => getConfigurationTabForStep(s.id) === tabId
        );
        result[tabId] = firstSection
          ? !isConfigStepAccessible(configurationSections, firstSection.id)
          : false;
      }
    );
    return result;
  }, [configurationSections]);

  const tabStatuses = useMemo(
    () =>
      getConfigurationTabStatuses(activeConfiguration, configurationSections),
    [activeConfiguration, configurationSections]
  );

  const ductlessSidePanelTabs: SidePanelTab[] = useMemo(
    () => [
      {
        id: 'condensers',
        label: 'Place condensers',
        disabled: tabDisabled.condensers,
        summary: tabStatuses.condensers.summary,
        complete: tabStatuses.condensers.complete,
        renderContent: () => (
          <div className='flex flex-col gap-4 p-3'>
            <CondenserList disabled={isViewOnlyConfiguration} />
          </div>
        )
      },
      {
        id: 'zones',
        label: 'Assign zones',
        disabled: tabDisabled.zones,
        summary: tabStatuses.zones.summary,
        complete: tabStatuses.zones.complete,
        renderContent: () => (
          <div className='flex flex-col gap-4 p-3'>
            <ZoneCondenserMap
              activeZoneId={activeZoneId}
              isOnZoneStep={isOnZoneStep}
              readOnly={isViewOnlyConfiguration}
              onSetActiveZone={handleZoneSelect}
              onRemoveSpaceFromZone={handleRemoveSpaceFromZone}
              onDeleteZone={handleDeleteZone}
              onAddZoneToCondenser={handleAddZoneToCondenser}
            />
          </div>
        )
      },
      {
        id: 'air_handlers',
        label: 'Place air handlers',
        disabled: tabDisabled.air_handlers,
        summary: tabStatuses.air_handlers.summary,
        complete: tabStatuses.air_handlers.complete,
        renderContent: () => (
          <div id='air-handler-list' className='flex flex-col gap-4 p-3'>
            <AirHandlerList disabled={isViewOnlyConfiguration} />
          </div>
        )
      },
      {
        id: 'line_sets',
        label: 'Connect line sets',
        disabled: tabDisabled.line_sets,
        summary: tabStatuses.line_sets.summary,
        complete: tabStatuses.line_sets.complete,
        renderContent: () => {
          const visibleCondensers = activeConfiguration
            ? getVisibleCondensers(activeConfiguration)
            : [];
          const airHandlers = activeConfiguration
            ? getAirHandlersFromConfig(
                activeConfiguration,
                structure,
                concealedDuctLayoutMap
              )
            : [];
          const canAutoGenerate =
            visibleCondensers.length > 0 && airHandlers.length > 0;
          const configurationId = activeConfiguration?.uniqueId;

          return (
            <div className='flex flex-col gap-4 p-3'>
              {!isViewOnlyConfiguration && (
                <>
                  <div id='auto-generate-linesets' className='flex flex-col'>
                    <Button
                      disabled={!canAutoGenerate || !configurationId}
                      icon={SparklesIcon}
                      iconSize='sm'
                      onClick={() =>
                        configurationId
                          ? dispatch(autoGenerateLinesets({ configurationId }))
                          : undefined
                      }
                      text='Auto-generate line sets'
                      fullWidth
                    />
                  </div>
                  <ResetLinesetsButton />
                  <div className='text-center text-sm text-gray-500'>
                    Right click on a condenser or air handler to generate a line
                    set.
                  </div>
                </>
              )}
              <LinesetSummaryPanel />
            </div>
          );
        }
      }
    ],
    [
      handleAddZoneToCondenser,
      activeZoneId,
      isOnZoneStep,
      isViewOnlyConfiguration,
      handleRemoveSpaceFromZone,
      handleDeleteZone,
      handleZoneSelect,
      activeConfig,
      configurations,
      structure,
      dispatch,
      isAwaitingAirHandlerAvailability,
      tabDisabled,
      tabStatuses
    ]
  );

  const ptacSidePanelTabs: SidePanelTab[] = useMemo(
    () => [
      {
        id: 'zones',
        label: 'Assign zones',
        disabled: tabDisabled.zones,
        summary: tabStatuses.zones.summary,
        complete: tabStatuses.zones.complete,
        renderContent: () => (
          <div className='flex flex-col gap-4 p-3'>
            <ZoneCondenserMap
              activeZoneId={activeZoneId}
              isOnZoneStep={isOnZoneStep}
              readOnly={isViewOnlyConfiguration}
              variant='flat'
              onSetActiveZone={handleZoneSelect}
              onRemoveSpaceFromZone={handleRemoveSpaceFromZone}
              onDeleteZone={handleDeleteZone}
              onAddZoneToCondenser={handleAddZoneToCondenser}
              onAddZone={handleAddPtacZone}
            />
          </div>
        )
      },
      {
        id: 'air_handlers',
        label: 'Place PTAC units',
        disabled: tabDisabled.air_handlers,
        summary: ptacUnitsPlacedSummary(activeConfiguration),
        complete: tabStatuses.air_handlers.complete,
        renderContent: () => (
          <div id='air-handler-list' className='flex flex-col gap-4 p-3'>
            <PtacUnitsPanel readOnly={isViewOnlyConfiguration} />
          </div>
        )
      }
    ],
    [
      tabDisabled,
      tabStatuses,
      isViewOnlyConfiguration,
      activeZoneId,
      isOnZoneStep,
      handleZoneSelect,
      handleRemoveSpaceFromZone,
      handleDeleteZone,
      handleAddZoneToCondenser,
      handleAddPtacZone
    ]
  );

  const sidePanelTabs = isPtacDesign
    ? ptacSidePanelTabs
    : ductlessSidePanelTabs;

  const handlePanelTabChange = useCallback(
    (tabId: string) => {
      setActivePanelTab(tabId);

      if (tabId === 'zones') {
        const zoneStep = activeZoneId
          ? configurationSections.find(
              (s) =>
                s.id.startsWith('add_spaces_zone_') &&
                s.steps?.[0]?.zoneUniqueId === activeZoneId
            )
          : configurationSections.find((s) =>
              s.id.startsWith('add_spaces_zone_')
            );
        if (zoneStep) {
          setCurrentSectionId(zoneStep.id);
        }
      } else if (tabId === 'condensers') {
        setCurrentSectionId('condensers');
      } else if (tabId === 'air_handlers') {
        setCurrentSectionId('air_handlers');
      } else if (tabId === 'line_sets') {
        setCurrentSectionId('line_sets');
      }
    },
    [activeZoneId, configurationSections, setCurrentSectionId]
  );

  const previousSectionIdForTabRef = useRef<string | null>(null);

  useEffect(() => {
    if (previousSectionIdForTabRef.current === currentSectionId) {
      return;
    }
    previousSectionIdForTabRef.current = currentSectionId;

    const tabForStep = getConfigurationTabForStep(currentSectionId);
    if (tabForStep) {
      setActivePanelTab(tabForStep);
    }
  }, [currentSectionId]);

  const prevSectionIdForZoneSyncRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevSectionIdForZoneSyncRef.current === currentSectionId) {
      return;
    }
    prevSectionIdForZoneSyncRef.current = currentSectionId;

    if (!currentSectionId.startsWith('add_spaces_zone_')) {
      return;
    }

    const currentStep = configurationSections.find(
      (s) => s.id === currentSectionId
    );
    const stepZoneId = currentStep?.steps?.[0]?.zoneUniqueId;
    setActiveZoneId(stepZoneId ?? null);
  }, [currentSectionId, configurationSections]);

  const previousActiveTabRef = useRef<string | null>(null);

  useEffect(() => {
    if (activeTab !== 'configurations') {
      previousActiveTabRef.current = activeTab;
      return;
    }

    const configUniqueId = activeConfiguration?.uniqueId ?? null;
    const wasOnConfigurations =
      previousActiveTabRef.current === 'configurations';
    const configChanged = activeConfigurationStepConfigId !== configUniqueId;
    const currentStepExists = configurationSections.some(
      (section) => section.id === currentSectionId
    );

    previousActiveTabRef.current = activeTab;

    if (!configChanged && (wasOnConfigurations || currentStepExists)) return;

    dispatch(setActiveConfigurationStepConfigId(configUniqueId));

    setCurrentSectionId(
      resolveConfigurationEntryStepId(configurationSections, hasUnzonedSpaces)
    );
    equipmentResetRef.current = false;
  }, [
    activeTab,
    activeConfiguration,
    activeConfigurationStepConfigId,
    configurationSections,
    currentSectionId,
    hasUnzonedSpaces,
    dispatch,
    setCurrentSectionId
  ]);

  useEffect(() => {
    if (currentSectionId !== 'zones') {
      equipmentResetRef.current = false;
    }
  }, [currentSectionId]);

  const { closeContextMenu } = useContextMenu();

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      closeContextMenu();
      setActiveId(event.active.id as string);
      setActiveDragData(event.active.data.current);

      const dragData = event.active.data.current;
      if (isCondenserOrAirHandlerDragType(dragData?.type)) {
        setShow3DView(false);
      }

      if (dragData?.type === 'grid-space' && dragData?.sectionId) {
        const currentLevelSpaces =
          getGridSpacesForLevelCallback(selectedLevelIndex);
        const spacesInSameSection = currentLevelSpaces.filter(
          (space) => space.sectionId === dragData.sectionId
        );
        setDraggedSectionSpaces(spacesInSameSection);
      } else {
        setDraggedSectionSpaces([]);
      }
    },
    [selectedLevelIndex, getGridSpacesForLevelCallback, closeContextMenu]
  );

  const handleDragOver = useCallback(() => {}, []);

  const handleDragEnd = useCallback(
    (event: any) => {
      if (isViewOnlyConfiguration) {
        setActiveId(null);
        setActiveDragData(null);
        setDraggedSectionSpaces([]);
        return;
      }
      createHandleDragEnd({
        event,
        dispatch,
        structure,
        levels,
        selectedLevelIndex,
        configurations,
        activeConfigIndex: activeConfig,
        selectedSystems,
        draggedSectionSpaces,
        getGridSpacesForLevel: getGridSpacesForLevelCallback,
        setActiveId,
        setActiveDragData,
        setDraggedSectionSpaces,
        zoom: gridZoom,
        panOffset: gridPanOffset,
        blockAirHandlerDrops: isAwaitingAirHandlerAvailability
      });
    },
    [
      dispatch,
      configurations,
      activeConfig,
      selectedSystems,
      levels,
      selectedLevelIndex,
      draggedSectionSpaces,
      getGridSpacesForLevelCallback,
      structure,
      gridZoom,
      gridPanOffset,
      isAwaitingAirHandlerAvailability,
      isViewOnlyConfiguration
    ]
  );

  const handleTabDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || over.id === active.id) {
        return;
      }

      const fromIndex = parseInt(active.id as string, 10);
      const toIndex = parseInt(over.id as string, 10);

      if (!Number.isNaN(fromIndex) && !Number.isNaN(toIndex)) {
        dispatch(reorderLevels({ fromIndex, toIndex }));
        if (selectedLevelIndex === fromIndex) {
          dispatch(setActiveLevelIndex(toIndex));
        } else if (
          selectedLevelIndex > fromIndex &&
          selectedLevelIndex <= toIndex
        ) {
          dispatch(setActiveLevelIndex(selectedLevelIndex - 1));
        } else if (
          selectedLevelIndex < fromIndex &&
          selectedLevelIndex >= toIndex
        ) {
          dispatch(setActiveLevelIndex(selectedLevelIndex + 1));
        }
      }
    },
    [dispatch, selectedLevelIndex]
  );

  const tabSensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5
      }
    })
  );

  const getNextZoneDisabledReason = (): string | undefined => {
    if (isViewOnlyConfiguration || !isOnZoneStep) return undefined;
    if (!currentZoneHasSpaces && hasUnzonedSpaces) {
      return 'Add spaces to the current zone before adding another';
    }
    return undefined;
  };
  const nextZoneDisabledReason = getNextZoneDisabledReason();

  const getNextSectionBlockedReason = (): string | undefined => {
    if (hasPtacDesignErrors) return PTAC_DESIGN_ISSUES_REASON;
    if (waitingForValidCombinations) return SYSTEM_PREVIEW_MISSING_REASON;
    if (!hasEligibleConfiguration) {
      if (!isDuctedDesign) return NO_ELIGIBLE_CONFIGURATION_REASON;
      return ductedDesignIncompleteReason(ductedPairLinkMode);
    }
    return undefined;
  };
  const nextSectionBlockedReason = getNextSectionBlockedReason();

  return (
    <DndContext
      sensors={sensors}
      modifiers={[snapModifier]}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className='mx-auto flex h-full max-w-[1700px] flex-col gap-5'>
        <div className='min-h-0 grow'>
          <StepSidebar
            sections={sidebarSections}
            currentSectionId={currentSectionId}
            setCurrentSectionId={setCurrentSectionId}
            onSectionClick={handleDeleteEmptyZonesOnLeave}
            configurationUniqueId={activeConfiguration?.uniqueId}
            centeredHeader
            hideHeaderDescription
            panelWidth={panelWidth}
            previousTab='structure'
            nextTab='chooseSystems'
            onNext={handleNextStep}
            nextSectionDisabled={nextSectionBlockedReason !== undefined}
            nextSectionDisabledReason={nextSectionBlockedReason}
            nextSectionAlwaysEnabled={
              hasEligibleConfiguration && !hasPtacDesignErrors
            }
            // Soft pre-warm readiness on the hop to Choose Systems (AHA-6681
            // sub-build 5): purely informational, never disables.
            nextSectionAdornment={<DuctedReadinessIndicator />}
            nextButtonText={
              !isViewOnlyConfiguration &&
              isOnZoneStep &&
              (!isLastZoneStep || (!hasAirHandlers && hasUnzonedSpaces))
                ? 'Next Zone'
                : undefined
            }
            nextButtonDisabled={
              !isViewOnlyConfiguration &&
              isOnZoneStep &&
              !hasAirHandlers &&
              !currentZoneHasSpaces &&
              hasUnzonedSpaces
            }
            nextButtonDisabledReason={nextZoneDisabledReason}
            alternateNextButton={
              !isViewOnlyConfiguration &&
              isOnZoneStep &&
              !hasAirHandlers &&
              hasUnzonedSpaces
                ? {
                    text: 'Done With Zones',
                    onClick: handleNextAllZonesAssigned,
                    disabled: !hasAnyZonedSpaces,
                    disabledReason: !hasAnyZonedSpaces
                      ? 'You need at least one zone on a configuration'
                      : undefined
                  }
                : undefined
            }
            tabs={sidePanelTabs}
            activePanelTab={activePanelTab}
            onPanelTabChange={handlePanelTabChange}
            panelVariant='accordion'
            viewOnlyMode={
              isViewOnlyConfiguration && activeConfiguration
                ? {
                    title: activeConfiguration.name,
                    editButtonText: 'Edit this configuration',
                    onEdit: handleEditActiveConfiguration
                  }
                : undefined
            }
            rightContent={
              <div className='flex h-full w-full gap-3'>
                <div className='flex min-w-0 flex-1 flex-col gap-0'>
                  <ConfigurationTabs
                    onSelectConfiguration={handleSelectConfiguration}
                  />
                  <div className='relative min-h-0 flex-1'>
                    <div className='relative h-full w-full'>
                      <DndContext
                        sensors={tabSensors}
                        onDragEnd={handleTabDragEnd}
                      >
                        <LevelPicker currentSectionId={currentSectionId} />
                      </DndContext>
                      <div className='absolute right-2 top-2 z-20 flex flex-col items-end gap-2 2xl:flex-row 2xl:items-start 2xl:gap-3'>
                        <Popover className='relative'>
                          <Popover.Button className='relative inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'>
                            <AdjustmentsHorizontalIcon className='h-7 w-7' />
                            <Tooltip text='Settings' />
                          </Popover.Button>
                          <Popover.Panel className='absolute right-0 top-12 z-30 w-64 rounded-lg bg-white p-4 shadow-lg'>
                            <div className='flex flex-col gap-4'>
                              <div className='flex flex-col gap-2'>
                                <div className='flex items-center justify-between'>
                                  <span className='text-sm font-medium text-gray-700'>
                                    Spaces
                                  </span>
                                  <span className='text-sm text-gray-500'>
                                    {spaceOpacity}%
                                  </span>
                                </div>
                                <input
                                  type='range'
                                  min='0'
                                  max='100'
                                  value={spaceOpacity}
                                  onChange={(e) =>
                                    setSpaceOpacity(
                                      Number.parseInt(e.target.value, 10)
                                    )
                                  }
                                  className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600'
                                />
                              </div>
                              {!show3DView && (
                                <>
                                  <div className='flex flex-col gap-2'>
                                    <div className='flex items-center justify-between'>
                                      <span className='text-sm font-medium text-gray-700'>
                                        Grid Lines
                                      </span>
                                      <span className='text-sm text-gray-500'>
                                        {gridLinesOpacity}%
                                      </span>
                                    </div>
                                    <input
                                      type='range'
                                      min='0'
                                      max='100'
                                      value={gridLinesOpacity}
                                      onChange={(e) =>
                                        setGridLinesOpacity(
                                          Number.parseInt(e.target.value, 10)
                                        )
                                      }
                                      className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600'
                                    />
                                  </div>
                                  <div className='flex flex-col gap-2'>
                                    <div className='flex items-center justify-between'>
                                      <span className='text-sm font-medium text-gray-700'>
                                        Spaces/Equipment Below
                                      </span>
                                      <span className='text-sm text-gray-500'>
                                        {overlaySpacesOpacity}%
                                      </span>
                                    </div>
                                    <input
                                      type='range'
                                      min='0'
                                      max='100'
                                      value={overlaySpacesOpacity}
                                      onChange={(e) =>
                                        setOverlaySpacesOpacity(
                                          Number.parseInt(e.target.value, 10)
                                        )
                                      }
                                      className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600'
                                    />
                                  </div>
                                </>
                              )}
                              {show3DView && (
                                <>
                                  <div className='flex flex-col gap-2'>
                                    <div className='flex items-center justify-between'>
                                      <span className='text-sm font-medium text-gray-700'>
                                        Other Levels Opacity
                                      </span>
                                      <span className='text-sm text-gray-500'>
                                        {inactiveLevelOpacity}%
                                      </span>
                                    </div>
                                    <input
                                      type='range'
                                      min='0'
                                      max='100'
                                      value={inactiveLevelOpacity}
                                      onChange={(e) =>
                                        setInactiveLevelOpacity(
                                          Number.parseInt(e.target.value, 10)
                                        )
                                      }
                                      className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600'
                                    />
                                  </div>
                                  <label className='flex cursor-pointer items-center gap-2 text-sm text-gray-700'>
                                    <input
                                      type='checkbox'
                                      checked={colorLinesetsByCondenser}
                                      onChange={(e) =>
                                        setColorLinesetsByCondenser(
                                          e.target.checked
                                        )
                                      }
                                      className='h-4 w-4 rounded border-gray-300 text-blue-600'
                                    />
                                    Color line sets by condenser
                                  </label>
                                </>
                              )}
                            </div>
                          </Popover.Panel>
                        </Popover>
                        <button
                          type='button'
                          className='relative inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                          onClick={() => setShow3DView(!show3DView)}
                        >
                          {show3DView ? (
                            <Squares2X2Icon className='h-7 w-7' />
                          ) : (
                            <CubeIcon className='h-7 w-7' />
                          )}
                          <Tooltip text={show3DView ? '2D View' : '3D View'} />
                        </button>
                      </div>
                      {show3DView ? (
                        <div className='relative h-full w-full overflow-hidden rounded-b-lg border border-gray-200'>
                          <Grid3DScene
                            spaces={spaces3D}
                            objects={allObjects3D}
                            structure={structure}
                            activeLevelIndex={selectedLevelIndex}
                            configurations={configurations}
                            activeConfigIndex={activeConfig}
                            inactiveLevelOpacity={inactiveLevelOpacity / 100}
                            configurationId={
                              configurations[activeConfig]?.uniqueId
                            }
                            concealedDuctLayouts={concealedDuctLayouts}
                            readOnly={isViewOnlyConfiguration}
                            spaceOpacity={spaceOpacity}
                            colorLinesetsByCondenser={colorLinesetsByCondenser}
                          />
                          <View3DHelpPopup />
                        </div>
                      ) : (
                        <StructureGrid
                          ref={structureGridRef}
                          gridSpaces={getGridSpacesForLevelCallback(
                            selectedLevelIndex
                          )}
                          overlaySpaces={getOverlaySpacesForLevelCallback(
                            selectedLevelIndex
                          )}
                          overlayObjects={getOverlayObjectsForLevelCallback(
                            selectedLevelIndex
                          )}
                          objects={getGridObjectsForLevelCallback(
                            selectedLevelIndex
                          )}
                          lineSets={
                            configurations[activeConfig]?.lineSets || []
                          }
                          concealedDuctLayouts={concealedDuctLayouts}
                          currentLevelIndex={selectedLevelIndex}
                          configurationId={
                            configurations[activeConfig]?.uniqueId
                          }
                          currentLevelUniqueId={
                            levels[selectedLevelIndex]?.uniqueId
                          }
                          designRuleIssues={filteredDesignRuleResults}
                          mode={
                            currentSectionId.startsWith('add_spaces_zone_')
                              ? 'zones'
                              : 'static'
                          }
                          readOnly={isViewOnlyConfiguration}
                          gridLinesOpacity={gridLinesOpacity}
                          overlaySpacesOpacity={overlaySpacesOpacity}
                          gridSpaceOpacity={spaceOpacity}
                          showSectionHighlighting={stepsWithZoneVisualsEnabled}
                          zoneOutlines={zoneOutlinesWithLabels}
                          onZoneSpaceClick={handleZoneSpaceClick}
                          zoom={gridZoom}
                          onZoomChange={setGridZoom}
                          panOffset={gridPanOffset}
                          onPanOffsetChange={setGridPanOffset}
                          enableContextMenuInStaticMode
                          contextMenuFilter={hideSpaceContextMenu}
                          getZoneValidation={getZoneValidation}
                          noTopRounding
                          hoveredZoneId={hoveredZoneId}
                          onZoneHover={setHoveredZoneId}
                          autoFit
                          autoFitBoundingBox={unifiedAutoFitBoundingBox}
                          autoFitPaddingTiles={10}
                          footprintPolygons={effectiveFootprintPolygons}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className='flex h-full shrink-0 flex-col gap-3'
                  style={{ width: panelWidth }}
                >
                  {isPtacDesign && (
                    <PtacSystemsSummaryCard
                      onOpenList={() => setPtacSystemsModalOpen(true)}
                    />
                  )}
                  {isDuctedDesign && activeConfiguration && (
                    <DuctedPairsSummaryCard
                      configuration={activeConfiguration}
                      readOnly={isViewOnlyConfiguration}
                    />
                  )}
                  {!isPtacDesign && !isDuctedDesign && (
                    <DuctedSystemsSummaryCard
                      validSystemCount={validSystemCount}
                      matchingSystemCount={matchingSystemCount}
                      isRefreshing={isRefreshingValidSystems}
                      metrics={metrics}
                      metricsLoading={metricsLoading}
                      fallbackPriceRange={priceRange}
                      listReady={systemsListReady}
                      onOpenList={(mode) => {
                        setSystemsModalMode(mode);
                        setSystemsModalOpen(true);
                      }}
                    />
                  )}
                  <div className='flex min-h-0 flex-1 flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4'>
                    <div className='flex shrink-0 items-center justify-between'>
                      <span className='text-sm font-semibold text-gray-900'>
                        {(() => {
                          const errorCount = filteredDesignRuleResults.filter(
                            (r) => isErrorSeverity(r.severity)
                          ).length;
                          const warningCount =
                            filteredDesignRuleResults.length - errorCount;
                          const parts: string[] = [];
                          if (errorCount > 0)
                            parts.push(
                              `${errorCount} Error${errorCount > 1 ? 's' : ''}`
                            );
                          if (warningCount > 0)
                            parts.push(
                              `${warningCount} Warning${
                                warningCount > 1 ? 's' : ''
                              }`
                            );
                          return parts.length > 0
                            ? parts.join(', ')
                            : 'Validation';
                        })()}
                      </span>
                      <button
                        type='button'
                        onClick={() => {
                          dispatch(
                            setPendingEvaluation({
                              issues: filteredDesignRuleResults.map((r) => ({
                                code: r.code,
                                name: r.name,
                                severity: r.severity,
                                message: r.message
                              }))
                            })
                          );
                          dispatch(setShowChatPanel(true));
                        }}
                        className='flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100'
                      >
                        <SparklesIcon className='h-4 w-4' />
                        Evaluate Configuration
                      </button>
                    </div>
                    <DesignRulePanel issues={filteredDesignRuleResults} />
                    {filteredDesignRuleResults.length === 0 && (
                      <div className='rounded-lg border border-green-200 bg-green-50 p-3'>
                        <div className='flex items-start gap-2 text-green-700'>
                          <svg
                            className='h-5 w-5 shrink-0'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M5 13l4 4L19 7'
                            />
                          </svg>
                          <span className='text-base'>No issues</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </div>
      <ConfigurationsDragOverlay
        activeId={activeId}
        activeDragData={activeDragData}
        draggedSectionSpaces={draggedSectionSpaces}
        structure={structure}
        showZones={showZones}
        easyMode={easyMode}
        zoom={gridZoom}
      />
      {isPtacDesign ? (
        <PtacSystemsListModal
          openState={[ptacSystemsModalOpen, setPtacSystemsModalOpen]}
        />
      ) : (
        <SystemsListModal
          openState={[systemsModalOpen, setSystemsModalOpen]}
          initialMode={systemsModalMode}
        />
      )}
      <ConfigurationSelectionModal
        openState={[showConfigSelectionModal, setShowConfigSelectionModal]}
        configurations={eligibleConfigurations}
        onConfirm={handleConfirmConfigSelection}
      />
    </DndContext>
  );
}
